import { graphql } from 'gatsby';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Container from '~/layout/Container';
import BaseLink from '~/components/base/BaseLink';
import Icon from '~/components/base/Icon';

import LeftNav from '~/components/header/LeftNav';
import FixedNav, { NavIconLink } from '~/components/header/FixedNav';
import MobileFooter from '~/components/header/MobileFooter';

import { HiddenSpan, mobileOnly, calcVw } from '~/styles/helpers';
import { transition } from '~/styles/transition';

import useScrollLock from '~/hooks/useScrollLock';
import useWindowSize from '~/hooks/useWindowSize';
import useOnKeyPress from '~/hooks/useOnKeyPress';
import { isBrowser } from '~/utilities/helpers';
import trackCustomEvent from '~/utilities/trackCustomEvent';

const NavWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.red};
    font-family: ${({ theme }) => theme.fonts.heading};
    z-index: ${({ theme }) => theme.zIndex.userAccountDropdown};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        background-color: ${({ theme }) => theme.colors.text.blue};
        position: relative;
        border-bottom: 1px solid ${({ theme }) => theme.colors.primary.white};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        border-bottom: 0;
    }
`;

const Nav = styled.nav`
    display: flex;
    gap: 15px;
    justify-content: space-between;
    padding: 15px 0;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 0;
    }
`;

const MobileIconsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    ${mobileOnly}
`;

const MobileTrigger = styled.button`
    height: 100%;
    float: right;
    z-index: ${({ theme }) => theme.zIndex.mobileTrigger};
`;

const MainMenu = styled.div`
    display: ${({ isMenuOpen }) => (isMenuOpen ? 'flex' : 'none')};
    position: absolute;
    top: 0;
    left: 0;
    width: ${calcVw(100)};
    height: 100vh;
    z-index: ${({ theme }) => theme.zIndex.mobileMenu};
    background-color: ${({ theme }) => theme.colors.text.blue};
    padding: 70px 30px 270px;
    flex-direction: column;
    gap: 20px;
    overflow-y: scroll;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        flex-direction: row;
        height: auto;
        background-color: transparent;
        width: auto;
        position: static;
        display: flex;
        justify-content: space-between;
        gap: 0;
        white-space: nowrap;
        padding: 0;
        margin: 0 -20px;
        flex: 0 1 calc(100% - 200px);
        max-width: ${({ theme }) => theme.layout.containerWidth};
        z-index: ${({ theme }) => theme.zIndex.mainMenu};
        overflow-y: unset;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        flex: 0 1 calc(100% - 260px);
    }
`;

const NavList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        gap: 0;
        flex-direction: row;
        justify-content: flex-end;
        flex: 1 1 60%;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        justify-content: center;
    }
`;

const NavLink = styled(BaseLink)`
    ${transition({}, 'background-color')};
    display: flex;
    align-items: center;
    gap: 20px;
    font-size: 2.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.black};
    color: ${({ theme }) => theme.colors.primary.white};
    padding: 0;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        font-size: 2rem;
        padding: 15px 10px 10px;
        gap: 0;
        height: 100%;

        &:hover,
        &:focus {
            background-color: ${({ theme }) => theme.colors.accent.blue};
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding: 30px 10px;
        font-size: 1.7rem;
    }

    @media (min-width: 1400px) {
        padding: 30px 20px;
        font-size: 2rem;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        font-size: 2.3rem;
    }

    @media (min-width: 1710px) {
        font-size: 2.7rem;
        padding: 30px 25px;
    }
`;

const MobileOnlyIcon = styled(Icon)`
    ${mobileOnly}
`;

const MainNavigation = ({ data, simplifiedLayout = false }) => {
    const {
        wpMenu: { menuItems: mainMenuItems },
        allWpMenuItem: footerMenuItems,
        wp: {
            siteSettings: {
                siteSettings: { logo, mobileLogo, fixedNavLinks, socialLinks }
            }
        }
    } = data;

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navRef = useRef(null);
    const triggerRef = useRef(null);
    const { setIsLocked } = useScrollLock(navRef);
    const screenSizes = useWindowSize();

    useEffect(() => {
        setIsLocked(isMenuOpen);
    }, [isMenuOpen, setIsLocked]);

    useEffect(() => {
        if (screenSizes.includes('largeTablet')) {
            setIsMenuOpen(false);
        }
    }, [screenSizes]);

    // Keyboard Navigation: Close menu if open on escape and focus mobile trigger in header
    useOnKeyPress('Escape', (e) => {
        if (isBrowser && isMenuOpen) {
            setIsMenuOpen(false);

            if (triggerRef?.current) {
                triggerRef.current.focus();
            }
        }
    });

    // Keyboard Navigation: Prevent user from navigating outside of mobile menu
    useOnKeyPress('Tab', (e) => {
        if (isBrowser && isMenuOpen && triggerRef?.current) {
            if (e.shiftKey && document.activeElement === triggerRef.current) {
                e.preventDefault();
            }
        }
    });

    return (
        <NavWrapper>
            <Container maxWidth="maxWidth">
                <Nav>
                    <LeftNav {...{ logo, mobileLogo }} />

                    <MobileIconsWrapper>
                        <NavIconLink to="/cart">
                            <Icon icon="shopping-bag" size={30} color="black" />
                        </NavIconLink>

                        <MobileTrigger
                            aria-controls="main-navigation"
                            aria-expanded={isMenuOpen}
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            ref={triggerRef}
                        >
                            <HiddenSpan>
                                {isMenuOpen
                                    ? 'Close Navigation'
                                    : 'Main Navigation'}
                            </HiddenSpan>
                            <Icon
                                icon={isMenuOpen ? 'close' : 'hamburger'}
                                size={30}
                                color={isMenuOpen ? 'yellow' : 'black'}
                            />
                        </MobileTrigger>
                    </MobileIconsWrapper>

                    <MainMenu
                        id="main-navigation"
                        aria-label="Main"
                        {...{ isMenuOpen }}
                        ref={navRef}
                    >
                        {!simplifiedLayout && (
                            <NavList>
                                {mainMenuItems?.nodes?.length
                                    ? mainMenuItems.nodes.map(
                                          ({
                                              id,
                                              url,
                                              label,
                                              mainMenuOptions: { icon }
                                          }) => (
                                              <li key={id}>
                                                  <NavLink
                                                      to={url}
                                                      onClick={() =>
                                                          trackCustomEvent(
                                                              'main_navigation_click',
                                                              { value: url }
                                                          )
                                                      }
                                                  >
                                                      {icon ? (
                                                          <MobileOnlyIcon
                                                              {...{ icon }}
                                                              size={27}
                                                              sizeTablet={20}
                                                              sizeMobile={27}
                                                              color="yellow"
                                                          />
                                                      ) : null}
                                                      {label}
                                                  </NavLink>
                                              </li>
                                          )
                                      )
                                    : null}
                            </NavList>
                        )}

                        <FixedNav
                            {...{ simplifiedLayout, triggerRef }}
                            links={fixedNavLinks}
                        />

                        <MobileFooter
                            {...{
                                socialLinks,
                                footerMenuItems,
                                navRef,
                                triggerRef,
                                isMenuOpen
                            }}
                        />
                    </MainMenu>
                </Nav>
            </Container>
        </NavWrapper>
    );
};

MainNavigation.propTypes = {
    data: PropTypes.object.isRequired,
    simplifiedLayout: PropTypes.bool
};

export default MainNavigation;

export const query = graphql`
    query MainNavigation {
        wpMenu(locations: { eq: MAIN_MENU }) {
            menuItems {
                nodes {
                    label
                    url
                    id
                    mainMenuOptions {
                        icon
                    }
                }
            }
        }
        allWpMenuItem(
            filter: {
                locations: { eq: FOOTER_MENU }
                parentDatabaseId: { eq: 0 }
            }
            sort: { order: ASC }
        ) {
            nodes {
                url
                label
                id
                footerMenuOptions {
                    excludeFromMobile
                }
                childItems {
                    nodes {
                        url
                        label
                        id
                    }
                }
            }
        }
        wp {
            siteSettings {
                siteSettings {
                    socialLinks {
                        linkUrl
                        iconClass
                    }
                    fixedNavLinks {
                        link {
                            url
                            title
                            target
                        }
                        loggedInLink {
                            url
                            title
                            target
                        }
                        icon
                    }
                    logo {
                        altText
                        sourceUrl
                        localFile {
                            childImageSharp {
                                gatsbyImageData(width: 268)
                            }
                        }
                    }
                    mobileLogo {
                        altText
                        sourceUrl
                        localFile {
                            childImageSharp {
                                gatsbyImageData(width: 516)
                            }
                        }
                    }
                }
            }
        }
    }
`;
